import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {SimpleSearch} from '../../../../../reduxToolkit/Search/SearchApi';
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import TableFunction from "../../../../../components/table/TableFunction";
import {permissionObj} from "../../../../../Helpers/permission";
import {
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import {TABLES} from "../../../../../Apis/Tables";
import {EMPTY_VALUES, errorMsg, getValueByKey} from "../../../../../Apis/functions";
import {SIOS_TYPE} from "../../SIOs/Model/Models";
import {OUTPUT_NUMBER_DATA} from "../Model/Models";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import CustomTooltip from '../../../../../components/CustomTooltip';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * React hook that manages the main HID Outputs table logic.
 * Handles data fetching, pagination, deletion, and dynamic table rendering.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.selectedList - Array of selected record IDs for bulk actions
 * @param {Function} props.setSelectedList - Function to update the selected records state
 *
 * @returns {Object} - Exposes table configuration, pagination, CRUD actions, and helper methods
 */
const MainOutputTableApp = ({ selectedList, setSelectedList }) => {
    const dispatch = useDispatch();

    /** --- Local State --- */
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [search, setSearch] = useState(false);
    const [searchElement, setSearchElement] = useState('');
    const [bodyForExport, setBodyForExport] = useState('');
    const [results, setResults] = useState([]);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [modified, setModified] = useState(false);
    const tableId = "tbl_mot_table";
    const searchId = "txt_mot_search";

    /** --- Redux Selectors --- */
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);

    /** --- Permission and Table Config --- */
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_HID_DELETE));
    const leftPosition = deletePermission ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;
    const option = TABLES.HID_OUTPUT;

    /**
     * Builds the table header structure dynamically.
     *
     * @function
     * @returns {JSX.Element[]} Array of <CustomTh> components
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head" fixedXY={true} left={leftPosition}>{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('output_number')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('controller')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('sio')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('sio_type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('default_pulse')}</CustomTh>,
        ];
    };

    /**
     * Builds the table body rows dynamically from API results.
     *
     * @function
     * @returns {Object} Object containing `body` (JSX rows) and raw `data`
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) {
            return { body: [], data: [] };
        }

        const body = results?.content?.map((item) => (
            <>
                <CustomTd tableId={tableId} classes='first-column' fixed={true} left={leftPosition}>
                    <CustomTooltip
                        text={item?.name || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>{getValueByKey(item?.number, 'name', OUTPUT_NUMBER_DATA)}</CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.hidName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.sioName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>{t(getValueByKey(item?.sioType, 'name', SIOS_TYPE))}</CustomTd>
                <CustomTd tableId={tableId}>{item?.defaultPulse}</CustomTd>
            </>
        ));

        return { body, data: results?.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key:searchElement });
        setBodyForExport(body);

        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResults(data);
            }
        });
    }

    /**
     * Hook from TableFunction that provides table state, pagination, and selection logic.
     */
    const { t, thead, tbody, handleCheckboxChange, handleDeleteAll,
        pagination, page, rowsPerPage } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'outputs_per_page',
        setIsAllChecked,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes all selected HID Outputs using their IDs.
     * Refreshes the table upon success and displays a toast notification.
     *
     * @function
     */
    const deleteSelectedOutputs = () => {
        const body = selectedList;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: option, body })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true);
                }else errorMsg(code);
            });
        }
    };

    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        page,
        rowsPerPage,
        setResults,
        option,
        searchElement,
        setSearchElement,
        setSearch,
        setBodyForExport,
        setSimpleSearch,
        deletePermission,
        isAllChecked,
        setIsAllChecked,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedOutputs
    };
};

export default MainOutputTableApp;
